import { Link, useLocation } from "wouter";
import logoPath from "@assets/logo.webp";
import { FaInstagram, FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { Menu, X, Heart, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";

type NavItem =
  | { href: string; label: string }
  | { label: string; children: { href: string; label: string; external?: boolean }[] };

export default function Layout({ children }: { children: React.ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
    window.scrollTo(0, 0);
  }, [location]);

  const navItems: NavItem[] = [
    {
      label: "About",
      children: [
        { href: "/about", label: "About Us" },
        { href: "/our-team", label: "Our Team" },
        { href: "/join-our-team", label: "Join Our Team" },
      ],
    },
    {
      label: "Services",
      children: [
        { href: "/menu", label: "Our Services" },
        { href: "/vip-program", label: "VIP Program" },
        { href: "/service-areas", label: "Service Areas" },
        { href: "/policies-rates", label: "Company Policies" },
      ],
    },
    {
      label: "Resources",
      children: [
        { href: "/care-plan-finder", label: "Care Plan Finder" },
        { href: "/resources/summer-safety", label: "Summer Dog Safety" },
        { href: "/resources/hurricane-prep", label: "Hurricane Prep" },
        { href: "/resources/new-pet-welcome-kit", label: "New Pet Welcome Kit" },
        { href: "/resources", label: "All Resources" },
      ],
    },
    {
      label: "Community",
      children: [
        { href: "/lend-a-leash/", label: "Lend A Leash", external: true },
        { href: "/active-retiree-program", label: "Active Retiree Program" },
        { href: "/giving", label: "Giving Back" },
        { href: "/gallery", label: "Photo Gallery" },
        { href: "/blog", label: "Tally Lassie Times" },
      ],
    },
    { href: "/reviews", label: "Reviews" },
    { href: "/faqs", label: "FAQs" },
    { href: "/contact-us", label: "Contact" },
  ];

  function isDropdownActive(item: NavItem): boolean {
    if ("children" in item) {
      return item.children.some((child) => location === child.href);
    }
    return false;
  }

  function handleMouseEnter(label: string) {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  }

  function handleMouseLeave() {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  }

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <div className="bg-primary text-primary-foreground py-2 px-4 text-center text-sm font-medium">
        Proud to hold a 100% reliability guarantee for pet sitting and solo dog walking!
      </div>

      <header className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${scrolled ? "bg-white/98 backdrop-blur-md shadow-md border-border/40" : "bg-white border-transparent shadow-sm"}`}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3">
            <img src={logoPath} alt="Tallahassee Lassie Pet Care" width={700} height={547} className="h-14 w-auto object-contain" />
            <span className="font-serif text-xl font-bold text-primary hidden sm:inline-block">
              Tallahassee Lassie
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5">
            {navItems.map((item) => {
              if ("children" in item) {
                const active = isDropdownActive(item);
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`text-sm font-medium transition-colors flex items-center gap-1 relative after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
                        active
                          ? "text-primary after:w-full"
                          : "text-muted-foreground hover:text-primary after:w-0 hover:after:w-full"
                      }`}
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      aria-expanded={openDropdown === item.label}
                    >
                      {item.label}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${openDropdown === item.label ? "rotate-180" : ""}`}
                      />
                    </button>

                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
                        openDropdown === item.label
                          ? "opacity-100 translate-y-0 pointer-events-auto"
                          : "opacity-0 -translate-y-1 pointer-events-none"
                      }`}
                    >
                      <div
                        className="bg-white border border-border/60 shadow-xl py-2 min-w-[200px]"
                        style={{ borderRadius: "16px 0 16px 0" }}
                      >
                        {item.children.map((child) =>
                          child.external ? (
                            <a
                              key={child.href}
                              href={child.href}
                              className="block px-5 py-2.5 text-sm transition-colors text-muted-foreground hover:text-primary hover:bg-primary/5"
                            >
                              {child.label}
                            </a>
                          ) : (
                            <Link
                              key={child.href}
                              href={child.href}
                              className={`block px-5 py-2.5 text-sm transition-colors ${
                                location === child.href
                                  ? "text-primary bg-primary/5 font-medium"
                                  : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                              }`}
                            >
                              {child.label}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`text-sm font-medium transition-colors relative after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
                    location === item.href
                      ? "text-primary after:w-full"
                      : "text-muted-foreground hover:text-primary after:w-0 hover:after:w-full"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Button asChild variant="secondary" className="ml-4 shadow-sm font-serif tracking-wide">
              <a href="https://www.timetopet.com/portal/tallahassee-lassie/login" target="_blank" rel="noopener noreferrer">
                Client Login
              </a>
            </Button>
          </nav>

          <button
            className="lg:hidden p-2 text-primary"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-border p-4 flex flex-col gap-1 shadow-lg max-h-[80vh] overflow-y-auto">
            {navItems.map((item) => {
              if ("children" in item) {
                const expanded = mobileExpanded === item.label;
                const active = isDropdownActive(item);
                return (
                  <div key={item.label}>
                    <button
                      className={`w-full text-left text-lg font-serif font-medium p-3 transition-colors flex items-center justify-between ${
                        active ? "text-primary bg-primary/5" : "text-foreground hover:bg-muted"
                      }`}
                      style={{ borderRadius: "8px 0 8px 0" }}
                      onClick={() => setMobileExpanded(expanded ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {expanded && (
                      <div className="ml-4 border-l-2 border-primary/20 pl-2 mb-1">
                        {item.children.map((child) =>
                          child.external ? (
                            <a
                              key={child.href}
                              href={child.href}
                              className="block text-base font-serif p-3 transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
                              style={{ borderRadius: "8px 0 8px 0" }}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {child.label}
                            </a>
                          ) : (
                            <Link
                              key={child.href}
                              href={child.href}
                              className={`block text-base font-serif p-3 transition-colors ${
                                location === child.href
                                  ? "text-primary bg-primary/5 font-medium"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                              }`}
                              style={{ borderRadius: "8px 0 8px 0" }}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {child.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`text-lg font-serif font-medium p-3 transition-colors ${
                    location === item.href
                      ? "text-primary bg-primary/5"
                      : "text-foreground hover:bg-muted"
                  }`}
                  style={{ borderRadius: "8px 0 8px 0" }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              );
            })}
            <Button asChild className="w-full mt-2 font-serif text-lg py-6 shadow-sm">
              <a href="https://www.timetopet.com/portal/tallahassee-lassie/login" target="_blank" rel="noopener noreferrer">
                Client Login
              </a>
            </Button>
          </div>
        )}
      </header>

      <main className="flex-1">
        {children}
      </main>

      <footer className="bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/16617bc9-4b44-4480-8f95-5470f189f37e/IMG_4166_jpg.jpg')] bg-cover bg-center opacity-[0.04]"></div>
        <div className="absolute top-0 left-0 w-full h-3 bg-secondary"></div>

        <div className="container mx-auto px-4 pt-20 pb-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="col-span-1 md:col-span-2">
              <Link href="/" className="flex items-center gap-3 mb-6">
                <div className="bg-white p-2" style={{ borderRadius: "16px 0 16px 0" }}>
                  <img src={logoPath} alt="Tallahassee Lassie Logo" width={700} height={547} className="h-12 w-auto" />
                </div>
                <span className="font-serif text-2xl font-bold text-white">
                  Tallahassee Lassie
                </span>
              </Link>
              <p className="text-primary-foreground/80 max-w-md text-lg leading-relaxed mb-8 font-serif">
                Trusted neighborhood pet care with real heart. 
                We offer dog walking, pet sitting, and more, with no breed restrictions.
              </p>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/tallahassee_lassie/" target="_blank" rel="noopener noreferrer" className="p-3 bg-white/10 hover:bg-white/25 transition-all hover:scale-105" style={{ borderRadius: "12px 0 12px 0" }} aria-label="Follow us on Instagram">
                  <FaInstagram size={20} />
                </a>
                <a href="https://www.facebook.com/tallahasseelassie" target="_blank" rel="noopener noreferrer" className="p-3 bg-white/10 hover:bg-white/25 transition-all hover:scale-105" style={{ borderRadius: "12px 0 12px 0" }} aria-label="Follow us on Facebook">
                  <FaFacebookF size={20} />
                </a>
                <a href="https://www.linkedin.com/company/tallahassee-lassie/" target="_blank" rel="noopener noreferrer" className="p-3 bg-white/10 hover:bg-white/25 transition-all hover:scale-105" style={{ borderRadius: "12px 0 12px 0" }} aria-label="Connect on LinkedIn">
                  <FaLinkedinIn size={20} />
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl font-bold mb-6 flex items-center gap-2">
                <Heart size={18} className="text-secondary" fill="currentColor" /> Quick Links
              </h3>
              <ul className="space-y-3">
                <li><Link href="/about" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">About Us</Link></li>
                <li><Link href="/menu" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Our Services</Link></li>
                <li><Link href="/service-areas" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Service Areas</Link></li>
                <li><Link href="/policies-rates" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Company Policies</Link></li>
                <li><Link href="/contact-us" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Contact Us</Link></li>
                <li><a href="https://www.timetopet.com/portal/tallahassee-lassie/login" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Client Login</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-serif text-xl font-bold mb-6 flex items-center gap-2">
                <Heart size={18} className="text-secondary" fill="currentColor" /> Community
              </h3>
              <ul className="space-y-3">
                <li><Link href="/lend-a-leash" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Lend A Leash</Link></li>
                <li><Link href="/active-retiree-program" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Active Retiree Program</Link></li>
                <li><Link href="/giving" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Giving Back</Link></li>
                <li><Link href="/gallery" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Photo Gallery</Link></li>
                <li><Link href="/join-our-team" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Join Our Team</Link></li>
                <li><Link href="/blog" className="hover:text-secondary transition-colors underline-offset-4 hover:underline">Tally Lassie Times</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-16 pt-8 border-t border-white/10 text-center text-sm text-primary-foreground/60 flex flex-col md:flex-row justify-between items-center gap-4">
            <p>&copy; {new Date().getFullYear()} Tallahassee Lassie Pet Care LLC. All rights reserved.</p>
            <p className="text-primary-foreground/40">Tallahassee, FL</p>
          </div>
        </div>
      </footer>
      <a
        href="https://calendly.com/carla-tallylassie"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-5 py-3 shadow-lg hover:shadow-xl transition-all rounded-tl-2xl rounded-br-2xl rounded-tr-sm rounded-bl-sm"
        aria-label="Book a call with Carla"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
        <span className="hidden sm:inline">Book a Call</span>
      </a>
    </div>
  );
}
